// Shared Combat PRO spell target selector. DM and player spell surfaces both use
// the existing CombatContext spellTargets store and target tokens; this component
// owns presentation only and never resolves or applies a spell.

import React, { useContext } from 'react';
import { CombatContext } from '../../contexts/CombatContext';
import { deriveFullName } from '../../monsters/monsterEngine';
import { findPlayerById, getPlayerName } from '../../players/playerEngine';

export function effectiveSpellTargets(state, key, autoTargets) {
  const stored = state && state.spellTargets && state.spellTargets[key] ? state.spellTargets[key] : [];
  return stored.length ? stored : (Array.isArray(autoTargets) ? autoTargets : []);
}

export default function SpellTargetSelector(props) {
  const { state, spellTargetKey, toggleManualMode, setManualMode, setSpellTargets } = useContext(CombatContext);
  const casterKey = props.casterKey;
  const spellIdx = props.spellIdx;
  const key = spellTargetKey(casterKey, spellIdx);
  const effective = effectiveSpellTargets(state, key, props.autoTargets);
  const manualActive = Boolean(state.manualSelectMode && String(state.manualSelectMode.casterIndex) === String(casterKey) && state.manualSelectMode.spellIdx === spellIdx);
  const count = Math.min(state.activeMonsterCount || state.monsters.length, state.monsters.length);
  const monsters = state.monsters.slice(0, count).map(function (monster, index) {
    return { index: index, label: deriveFullName(monster, index), alive: Number(monster.currentHP || 0) > 0 };
  });
  const safeId = String(casterKey).replace(/[^a-z0-9_-]/gi, '_') + '-' + spellIdx;

  function commit(list) { setSpellTargets(casterKey, spellIdx, list); }
  function add(token) {
    if (token === '') { return; }
    const value = /^\d+$/.test(String(token)) ? Number(token) : token;
    const next = effective.slice();
    if (!next.some(function (target) { return String(target) === String(value); })) { next.push(value); }
    commit(next);
  }
  function label(token) {
    if (typeof token === 'string' && (token.indexOf('p:') === 0 || token.indexOf('c:') === 0)) {
      const id = parseInt(token.slice(2), 10);
      const player = findPlayerById(state.players, id);
      const index = state.players.findIndex(function (candidate) { return candidate.id === id; });
      return getPlayerName(player, index < 0 ? 0 : index);
    }
    const index = Number(token);
    return state.monsters[index] ? deriveFullName(state.monsters[index], index) : ('Monster ' + (index + 1));
  }
  function playerToken(player) { return (player.companion ? 'c:' : 'p:') + player.id; }

  return (
    <div className="vi-spell-target-block" id={'viTgtBlock-' + safeId}>
      <div className="vi-spell-target-label">Targets</div>
      <div className="vi-spell-target-pills" id={'viTgtPills-' + safeId}>
        {effective.length === 0
          ? <span style={{ fontSize: '12px', color: '#5A6B7E', fontStyle: 'italic' }}>No targets selected</span>
          : effective.map(function (target, index) { return (
            <button type="button" key={String(target) + ':' + index} className={manualActive ? 'vi-target-pill vi-target-pill-manual-mode' : 'vi-target-pill'} onClick={function () { commit(effective.filter(function (value) { return String(value) !== String(target); })); }}>
              {label(target)}<span className="vi-target-pill-x">✕</span>
            </button>
          ); })}
      </div>
      <div className="vi-spell-target-dropdowns">
        <div style={{ flex: '0 0 auto' }} data-target-monsters>
          <div className="vi-spell-target-label" style={{ marginBottom: '4px' }}>Target Monsters</div>
          <select aria-label="Target Monsters" className="vi-monster-selector-dropdown target-monster-dropdown" value="" onChange={function (event) { add(event.target.value); }}>
            <option value="">-- Select monster --</option>
            {monsters.map(function (monster) { return <option key={monster.index} value={monster.index}>{monster.label}</option>; })}
          </select>
        </div>
        <div style={{ flex: '0 0 auto' }} data-target-players>
          <div className="vi-spell-target-label" style={{ marginBottom: '4px' }}>Target Players</div>
          <select aria-label="Target Players" className="vi-monster-selector-dropdown target-player-dropdown" value="" onChange={function (event) { add(event.target.value); }}>
            {state.players.length === 0 ? <option value="">No players available</option> : <option value="">-- Select player or companion --</option>}
            {state.players.map(function (player, index) { return <option key={player.id} value={playerToken(player)}>{getPlayerName(player, index)}{player.companion ? ' (Companion)' : ''}</option>; })}
          </select>
        </div>
        {props.allowGroups ? <div className="cp-spell-target-groups">
          {props.selfTarget ? <button type="button" className="vi-tgt-btn" onClick={function () { commit([props.selfTarget]); }}>Self</button> : null}
          <button type="button" className="vi-tgt-btn" onClick={function () { commit(state.players.map(playerToken)); }}>Party</button>
          <button type="button" className="vi-tgt-btn" onClick={function () { commit(monsters.filter(function (monster) { return monster.alive; }).map(function (monster) { return monster.index; })); }}>Enemies / Area</button>
        </div> : null}
        <div className="vi-manual-hint">Use the current Combat PRO target store: select one or more valid targets, confirm, or use monster-tab manual targeting.</div>
      </div>
      <div className="vi-spell-target-btns">
        <button type="button" className="vi-tgt-btn vi-tgt-confirm" onClick={function () { commit(effective); setManualMode(null); }}>✓ Confirm</button>
        <button type="button" className="vi-tgt-btn vi-tgt-clear" onClick={function () { commit([]); setManualMode(null); }}>✕ Clear</button>
        {props.showManual === false ? null : <button type="button" className={manualActive ? 'vi-tgt-btn vi-tgt-manual active' : 'vi-tgt-btn vi-tgt-manual'} onClick={function () { toggleManualMode(casterKey, spellIdx); }}>⊕ Manual</button>}
      </div>
    </div>
  );
}
